import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import AISparkleIcon from 'components/Search/SearchForm/AISparkleIcon'
import noop from 'lib/function/noop'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Divider from 'LuxKit/Divider'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineArrowsSwapVerticalIcon from 'LuxKit/Icons/line/LineArrowsSwapVerticalIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import zIndex from 'styles/tools/z-index'
import TripListMoveDayMenuItem from './TripListMoveDayMenuItem'
import type { TripListMoveDayOption } from './tripListMoveDayOptions'

const Bar = styled.div`
  position: absolute;
  inset-inline: ${rem(20)};
  bottom: ${rem(84)};
  z-index: ${zIndex.floatingPanel};
  display: flex;
  justify-content: center;
  pointer-events: none;

  &.mobile {
    right: ${rem(12)};
    bottom: ${rem(24)};
    left: ${rem(12)};
  }
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  padding: ${rem(6)} ${rem(10)} ${rem(6)} ${rem(16)};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.darkBackground};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  box-shadow: ${props => props.theme.shadow.bottom.small};
  pointer-events: auto;

  &.mobile {
    width: fit-content;
    max-width: 100%;
    justify-content: flex-start;
    gap: ${rem(12)};
    padding: ${rem(8)} ${rem(10)};
    box-sizing: border-box;
  }
`

const CountText = styled(BodyText)`
  flex: 0 0 auto;
  white-space: nowrap;
`

const SeparatorSlot = styled.span`
  display: flex;
  align-items: stretch;
  height: ${rem(18)};
  margin-inline: ${rem(12)};
`

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0;

  &.mobile {
    gap: ${rem(6)};
    min-width: 0;
  }
`

const ActionButton = styled(TextButton)`
  --button-padding: ${rem(6)};
  font-weight: ${props => props.theme.font.secondary.weight.bold};

  &.mobile {
    min-width: 0;
    height: ${rem(40)};
    padding-right: ${rem(12)};
    padding-left: ${rem(12)};
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    border-radius: ${props => props.theme.borderRadius.round};
    color: ${props => props.theme.palette.neutral.surface.darkContrast};
    text-decoration: none;
    white-space: nowrap;

    &:hover,
    &:focus,
    &:active {
      text-decoration: none;
    }

    &.is-ask-ai-action {
      padding-right: ${rem(14)};
      padding-left: ${rem(10)};
    }
  }
`

const ActionSlot = styled.span`
  display: flex;
`

const MobileIconButton = styled(IconButton)`
  && {
    width: ${rem(40)};
    height: ${rem(40)};
    border: 1px solid ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    border-radius: ${props => props.theme.borderRadius.round};
    color: ${props => props.theme.palette.neutral.surface.darkContrast};
  }
`

interface BaseProps {
  selectedCount: number
  disabled?: boolean
  moveDayOptions: Array<TripListMoveDayOption>
  showMoveWithAi: boolean
  onAskAi: (origin?: { x: number; y: number }) => void
  onMoveToDay: (option: TripListMoveDayOption) => void | Promise<void>
  onMoveWithAi: (origin?: { x: number; y: number }) => void
  onDelete: () => void
}

interface DesktopProps extends BaseProps {
  variant?: 'desktop'
  onClear: () => void
}

interface MobileProps extends BaseProps {
  variant: 'mobile'
  showBookAction?: boolean
  onBook?: () => void
  onClear?: never
}

type Props = DesktopProps | MobileProps

function TripListQuickActions(props: Props) {
  const {
    selectedCount,
    disabled = false,
    moveDayOptions,
    showMoveWithAi,
    onAskAi,
    onMoveToDay,
    onMoveWithAi,
    onDelete,
  } = props
  const intl = useIntl()
  const [moveMenuOpen, setMoveMenuOpen] = useState(false)
  const moveTriggerRef = useRef<HTMLSpanElement>(null)
  const askAiTriggerRef = useRef<HTMLSpanElement>(null)
  const isMobile = props.variant === 'mobile'
  const showBookAction = isMobile ? props.showBookAction ?? false : false
  const onBook = isMobile ? props.onBook ?? noop : noop
  const showMoveMenu = moveDayOptions.length > 0
  const showMoveAction = showMoveMenu || showMoveWithAi

  useEffect(() => {
    if (disabled || !showMoveMenu) setMoveMenuOpen(false)
  }, [disabled, showMoveMenu])

  const closeMoveMenu = useCallback(() => {
    setMoveMenuOpen(false)
  }, [])

  // Launch point for the Ask AI context comet (see AiContextTrace), measured from
  // the toolbar button so it flies from where the user clicked to the chat chip.
  const getTriggerOrigin = useCallback((ref: RefObject<HTMLSpanElement | null>): { x: number; y: number } | undefined => {
    const rect = ref.current?.getBoundingClientRect()
    if (!rect) return undefined
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
  }, [])

  const handleMoveClick = useCallback(() => {
    if (disabled) return
    if (showMoveMenu) {
      setMoveMenuOpen(prev => !prev)
      return
    }
    if (showMoveWithAi) onMoveWithAi(getTriggerOrigin(moveTriggerRef))
  }, [disabled, getTriggerOrigin, onMoveWithAi, showMoveMenu, showMoveWithAi])

  const handleAskAiClick = useCallback(() => {
    if (disabled) return
    onAskAi(getTriggerOrigin(askAiTriggerRef))
  }, [disabled, getTriggerOrigin, onAskAi])

  const handleBookClick = useCallback(() => {
    if (disabled) return
    onBook()
  }, [disabled, onBook])

  const handleMoveDayOptionClick = useCallback((option: TripListMoveDayOption) => {
    closeMoveMenu()
    if (disabled) return
    onMoveToDay(option)
  }, [closeMoveMenu, disabled, onMoveToDay])

  const selectedCountLabel = intl.formatMessage(
    {
      id: 'R30GALHAubWLwIJQkilb',
      defaultMessage: '{count, plural, one {# selected} other {# selected}}',
      description: 'Selected row count shown in the trip planner list quick actions toolbar',
    },
    { count: selectedCount },
  )
  const deleteLabel = intl.formatMessage({
    id: '50fagiL_J8QYAU3W_ott',
    defaultMessage: 'Delete',
    description: 'Button label for deleting selected rows in the trip planner list quick actions toolbar',
  })
  const bookLabel = intl.formatMessage({
    id: 'ZKlOb-VxBzsMLnkO4v2r',
    defaultMessage: 'Book',
    description: 'Button label for booking selected rows in the mobile trip planner list quick actions toolbar',
  })
  const moveLabel = intl.formatMessage({
    id: 'AQy1-gEDiNMXFtS3SdH-',
    defaultMessage: 'Move',
    description: 'Button label for moving selected rows in the trip planner list quick actions toolbar',
  })
  const moveWithAiLabel = intl.formatMessage({
    id: 'GNElCVzFr2an2PESGDMV',
    defaultMessage: 'Move with AI',
    description: 'Button label for using AI to move selected rows in the trip planner list quick actions toolbar',
  })
  const moveToAnotherDayLabel = intl.formatMessage({
    id: 'HapGLjj4DK7cTzBFH3yJ',
    defaultMessage: 'Move to another day',
    description: 'Dropdown menu item for opening day move options in the trip planner list row',
  })
  const askAiLabel = intl.formatMessage({
    id: '8y-TWRLSiTNaLRDXx-2-',
    defaultMessage: 'Ask AI',
    description: 'Button label for asking AI about selected rows in the trip planner list quick actions toolbar',
  })
  const clearLabel = intl.formatMessage({
    id: '07XmzeISSDuAJNNkM-RY',
    defaultMessage: 'Clear selected rows',
    description: 'Accessible label for clearing selected rows in the trip planner list quick actions toolbar',
  })
  const moveActionLabel = showMoveWithAi ? moveWithAiLabel : moveLabel

  if (isMobile) {
    return (
      <Bar className="mobile">
        <Actions className="mobile">
          <CountText variant="medium" weight="bold">
            {selectedCountLabel}
          </CountText>
          <ActionGroup className="mobile">
            {showBookAction && (
              <ActionButton
                className="mobile is-book-action"
                kind="secondary"
                variant="ghost"
                size="medium"
                onClick={handleBookClick}
                disabled={disabled}
              >
                {bookLabel}
              </ActionButton>
            )}
            <ActionSlot ref={askAiTriggerRef}>
              <ActionButton
                className="mobile is-ask-ai-action"
                kind="tertiary"
                variant="ghost"
                size="medium"
                startIcon={<AISparkleIcon size="S" />}
                onClick={handleAskAiClick}
                disabled={disabled}
              >
                {askAiLabel}
              </ActionButton>
            </ActionSlot>
            {showMoveAction && (
              <ActionSlot ref={moveTriggerRef}>
                <MobileIconButton
                  kind="tertiary"
                  variant="ghost"
                  size="medium"
                  onClick={handleMoveClick}
                  disabled={disabled}
                  aria-label={moveActionLabel}
                >
                  <LineArrowsSwapVerticalIcon />
                </MobileIconButton>
              </ActionSlot>
            )}
            <MobileIconButton
              kind="tertiary"
              variant="ghost"
              size="medium"
              onClick={onDelete}
              disabled={disabled}
              aria-label={deleteLabel}
            >
              <LineTrashAltIcon />
            </MobileIconButton>
          </ActionGroup>
          <DropdownList
            size="M"
            open={moveMenuOpen}
            onClose={closeMoveMenu}
            anchorRef={moveTriggerRef}
            triggerRef={moveTriggerRef}
            placement="top"
            portaled
            width="min"
            title={moveToAnotherDayLabel}
            enableFlip
            autoHeight
          >
            {moveDayOptions.map(option => (
              <TripListMoveDayMenuItem
                key={option.id}
                option={option}
                onClick={handleMoveDayOptionClick}
              />
            ))}
          </DropdownList>
        </Actions>
      </Bar>
    )
  }

  return (
    <Bar>
      <Actions>
        <CountText variant="medium" weight="bold">
          {selectedCountLabel}
        </CountText>
        <SeparatorSlot>
          <Divider kind="secondary" direction="vertical" />
        </SeparatorSlot>
        <ActionGroup>
          <ActionButton
            kind="tertiary"
            variant="ghost"
            size="medium"
            startIcon={<LineTrashAltIcon />}
            onClick={onDelete}
            disabled={disabled}
          >
            {deleteLabel}
          </ActionButton>
          {showMoveAction && (
            <ActionSlot ref={moveTriggerRef}>
              <ActionButton
                kind="tertiary"
                variant="ghost"
                size="medium"
                startIcon={<LineArrowsSwapVerticalIcon />}
                onClick={handleMoveClick}
                disabled={disabled}
              >
                {moveActionLabel}
              </ActionButton>
            </ActionSlot>
          )}
          <ActionSlot ref={askAiTriggerRef}>
            <ActionButton
              kind="tertiary"
              variant="ghost"
              size="medium"
              startIcon={<AISparkleIcon size="S" />}
              onClick={handleAskAiClick}
              disabled={disabled}
            >
              {askAiLabel}
            </ActionButton>
          </ActionSlot>
        </ActionGroup>
        <DropdownList
          size="M"
          open={moveMenuOpen}
          onClose={closeMoveMenu}
          anchorRef={moveTriggerRef}
          triggerRef={moveTriggerRef}
          placement="top"
          portaled
          width="min"
          title={moveToAnotherDayLabel}
          enableFlip
          autoHeight
        >
          {moveDayOptions.map(option => (
            <TripListMoveDayMenuItem
              key={option.id}
              option={option}
              onClick={handleMoveDayOptionClick}
            />
          ))}
        </DropdownList>
        <SeparatorSlot>
          <Divider kind="secondary" direction="vertical" />
        </SeparatorSlot>
        <IconButton
          kind="tertiary"
          variant="ghost"
          size="medium"
          onClick={props.onClear}
          disabled={disabled}
          aria-label={clearLabel}
        >
          <LineTimesIcon />
        </IconButton>
      </Actions>
    </Bar>
  )
}

export default TripListQuickActions
